import * as React from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export interface PinInputProps {
  length?: 4 | 6;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (pin: string) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  autoFocus?: boolean;
  masked?: boolean;
}

export const PinInput: React.FC<PinInputProps> = ({
  length = 4,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  errorMessage,
  className,
  autoFocus = true,
  masked = true,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);

  // Focus input on mount if autoFocus
  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Track if we've already called onComplete for this value
  const lastCompletedRef = React.useRef<string | null>(null);

  // Trigger onComplete when PIN is filled (only once per unique complete value)
  React.useEffect(() => {
    if (value.length === length && onComplete && value !== lastCompletedRef.current) {
      lastCompletedRef.current = value;
      onComplete(value);
    }
    // Reset when value is cleared or becomes incomplete
    if (value.length < length) {
      lastCompletedRef.current = null;
    }
  }, [value, length, onComplete]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, "").slice(0, length);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow backspace to clear
    if (e.key === "Backspace" && value.length === 0) {
      return;
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  // Animation variants for dots
  const dotVariants = {
    empty: { scale: 1, opacity: 0.3 },
    filled: { scale: [1, 1.3, 1], opacity: 1 },
    error: { 
      x: [0, -8, 8, -8, 8, 0],
      transition: { duration: 0.4 }
    },
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Hidden input for keyboard */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        className="sr-only"
        aria-label="PIN input"
      />

      {/* Visual PIN dots */}
      <div
        onClick={handleContainerClick}
        className={cn(
          "flex items-center justify-center gap-4 cursor-text py-6",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        role="group"
        aria-label={`PIN input, ${value.length} of ${length} digits entered`}
      >
        {Array.from({ length }, (_, index) => {
          const isFilled = index < value.length;
          const isCurrentPosition = index === value.length && isFocused;

          return (
            <motion.div
              key={index}
              initial="empty"
              animate={error ? "error" : isFilled ? "filled" : "empty"}
              variants={dotVariants}
              className={cn(
                "relative flex items-center justify-center",
                "w-14 h-14 rounded-2xl border-2 transition-colors duration-200",
                isFilled
                  ? "border-primary bg-primary/10"
                  : isCurrentPosition
                  ? "border-primary bg-background"
                  : "border-muted-foreground/30 bg-muted/30",
                error && "border-destructive bg-destructive/10"
              )}
            >
              <AnimatePresence mode="wait">
                {isFilled && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className={cn(
                      "w-4 h-4 rounded-full",
                      error ? "bg-destructive" : "bg-primary"
                    )}
                  />
                )}
              </AnimatePresence>

              {/* Blinking cursor for current position */}
              {isCurrentPosition && !isFilled && (
                <motion.div
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="w-0.5 h-6 bg-primary rounded-full"
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && errorMessage && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-center text-sm text-destructive font-medium"
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PinInput;
